import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { FileUpload, ItemTemplateOptions } from 'primereact/fileupload'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../contexts'
import { useSelectFirstAndOnlyOption } from '../../../../../hooks'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../utils/helper/validateFormikFields'
import {
  containsDisallowedCharacters,
  getDisallowedCharacters,
} from '../../../../../utils/regex'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { FileUploadItemTemplate } from '../../../../invoice/components/Attachment/dialogs/upload/template'
import { useConfigContext } from '../../../../ui-config'
import { useDeliveryForecastDespatchAdviceById } from '../../../data'
import { requestAttachmentUpload } from '../../../data/api'

type Props = {
  despatchAdviceId: string
  processId: string
}

type FormValues = {
  attachment: File
  category: string
}

type FormFieldName = keyof FormValues

export const UploadAttachment: React.FC<Props> = ({
  despatchAdviceId,
  processId,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()
  const { data, mutate } =
    useDeliveryForecastDespatchAdviceById(despatchAdviceId)

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const [visible, setVisible] = useState(false)

  // ************************************************************
  // Formik
  // ************************************************************
  function hasSameName(filename: File) {
    const filesWithSameName =
      data?.Attachments.filter(
        attachment => attachment.Name === filename.name,
      ) || []

    return filesWithSameName.length > 0
  }

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      attachment: {} as File,
      category: '' as string,
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'attachment',
          config:
            config.deliveryForecastDespatchAdvice.uploadAttachment.attachment,
        },
        {
          fieldName: 'category',
          config:
            config.deliveryForecastDespatchAdvice.uploadAttachment.category,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      // attachment
      if (formData.attachment && hasSameName(formData.attachment!)) {
        errors.attachment = t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.attachment.validation.fileHasSameName',
        )
      }

      // Check filename for special characters
      const fileName = formData.attachment?.name
      const fileNameWithoutExtension = fileName?.slice(
        0,
        fileName.lastIndexOf('.'),
      )
      if (containsDisallowedCharacters(fileNameWithoutExtension)) {
        errors.attachment = t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.attachment.validation.attachmentContainsDisallowedCharacters',
          {
            disallowedCharacters: getDisallowedCharacters(
              fileNameWithoutExtension,
            ),
          },
        )
      }

      return errors
    },
    onSubmit: async formData => {
      await submitForm(formData)
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    const error = formik.errors[name]

    return (
      !isFormFieldValid(name) &&
      typeof error === 'string' && <small className="p-error">{error}</small>
    )
  }

  const submitForm = async (formData: FormValues) => {
    setIsRequestRunning(true)

    try {
      await requestAttachmentUpload(despatchAdviceId, processId, formData)

      mutate()

      handleClose()

      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.upload.messages.success.summary',
        ),
        detail: t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.upload.messages.success.detail',
          {
            name: formData.attachment?.name,
          },
        ),
      })
    } catch (error: any) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.upload.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // ************************************************************
  // Components
  // ************************************************************
  // attachment
  const attachmentLabel = (
    <label
      htmlFor="attachment"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('attachment'),
      })}
    ></label>
  )

  const uploadOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }
  const cancelOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }

  const onTemplateRemove = (callback: Function) => {
    formik.setFieldValue('attachment', null)
    callback()
  }

  const itemTemplate = (fileObject: any, props: ItemTemplateOptions) => {
    return (
      <FileUploadItemTemplate
        fileObject={fileObject}
        props={props}
        onTemplateRemove={onTemplateRemove}
      />
    )
  }

  const attachmentComponent = (
    <FileUpload
      id="attachment"
      uploadOptions={uploadOptions}
      cancelOptions={cancelOptions}
      itemTemplate={itemTemplate}
      emptyTemplate={t(
        'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.attachment.emptyMessage',
      )}
      onRemove={(event: any) => formik.setFieldValue('attachment', null)}
      onSelect={(event: any) =>
        formik.setFieldValue('attachment', event.files[0])
      }
      invalidFileSizeMessageSummary={t(
        'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.attachment.validation.invalidFileSize.summary',
      )}
      invalidFileSizeMessageDetail={t(
        'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.attachment.validation.invalidFileSize.detail',
      )}
    />
  )

  const attachmentErrorMsg = <div>{getFormErrorMessage('attachment')}</div>

  const attachmentWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.uploadAttachment}
      component={attachmentComponent}
      label={attachmentLabel}
      errorMsg={attachmentErrorMsg}
    />
  )

  // category
  const categoryLabel = (
    <label
      htmlFor="category"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('category'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'category',
    config.despatchAdvice.uploadAttachment.category.options,
    formik,
  )

  const categoryComponent = (
    <Dropdown
      id="category"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('category'),
      })}
      value={formik.values.category}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const categoryErrorMsg = <div>{getFormErrorMessage('category')}</div>

  const categoryWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.uploadAttachment}
      component={categoryComponent}
      label={categoryLabel}
      errorMsg={categoryErrorMsg}
    />
  )

  // deselect and close dialog
  const handleClose = () => {
    formik.resetForm()
    setVisible(false) // hide the dialog
  }

  // header
  const header = (
    <>
      {t(
        'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.title',
      )}
    </>
  )

  // footer
  const footer = (
    <>
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.cancel.label',
        )}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.dialog.form.upload.label',
        )}
        icon="pi pi-check"
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        type="submit"
        disabled={!formik.dirty || isRequestRunning}
      />
    </>
  )

  return (
    <>
      {config.despatchAdvice.uploadAttachment.enabled !== false && (
        <Button
          label={t(
            'deliveryForecastDespatchAdvice.attachment.additionalDocuments.table.upload.label',
          )}
          onClick={() => setVisible(true)}
          icon="pi pi-upload"
          className="p-button-outlined mb-2"
        />
      )}

      <Dialog
        visible={visible}
        style={{ width: '45rem' }}
        breakpoints={{ [calculateBreakpointWidth(45)]: '90vw' }}
        header={header}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="formgrid grid">
            <div className="col">{attachmentWrapper}</div>
          </div>

          <div className="formgrid grid">
            <div className="col">{categoryWrapper}</div>
          </div>
        </form>
      </Dialog>
    </>
  )
}
